// @file Managing and executing visual effects
import { checkOnNextPaintRecursively } from '@@/bits/dom'
import type { Engine, ISourceOptions } from '@tsparticles/engine'

// NOTE: Need to use throttle from es-tool/compat because the normal
// version doesn't support async functions
import { throttle } from 'es-toolkit/compat'

interface VisualEffectExecuteOptions {
  origin?: { x: number; y: number } | null
}

interface CustomVisualEffect {
  type: 'custom'
  id: string
  /**
   * Function to execute the visual effect, returns control object
   */
  execute: (
    target: HTMLElement,
    options?: VisualEffectExecuteOptions,
  ) => {
    /** Stops the visual effect */
    stop: () => void
    /** Promise that resolves when the visual effect is complete */
    promise: Promise<unknown>
  }
  /** @default 2000 (ms) */
  duration?: number
}

interface TsParticlesVisualEffect {
  type: 'tsParticles'
  id: string
  /**
   * Sets up the visual effect with the tsParticles engine
   * @param engine - tsParticles engine instance
   * @param shouldRefresh - If true, refreshes the engine. Ongoing effects will be stopped.
   */
  setup: (engine: Engine, shouldRefresh?: boolean) => Promise<void>
  /**
   * Computes the runtime options for the visual effect
   */
  getOptions: (engine: Engine, options?: VisualEffectExecuteOptions) => ISourceOptions
  /** @default 5000 (ms) */
  maxDuration?: number
}

type VisualEffect = CustomVisualEffect | TsParticlesVisualEffect

const MAX_CONCURRENT_EFFECTS = 5 // per effect type

class VfxManager {
  private readonly effects: Map<string, VisualEffect> = new Map()
  private readonly runningControls = new Set<() => void>()
  private readonly runningEffectCounts = new Map<string, number>()

  private addRunningEffect(id: string, stopFunc: () => void): void {
    this.runningControls.add(stopFunc)
    this.runningEffectCounts.set(id, (this.runningEffectCounts.get(id) ?? 0) + 1)
  }

  private removeRunningEffect(id: string, stopFunc: () => void): void {
    this.runningControls.delete(stopFunc)
    const newCount = (this.runningEffectCounts.get(id) ?? 1) - 1
    if (newCount <= 0) {
      this.runningEffectCounts.delete(id)
    } else {
      this.runningEffectCounts.set(id, newCount)
    }
  }

  private getRenderTarget(): HTMLElement {
    const portalTarget = document.getElementById('special-effects-portal-target')
    return portalTarget ?? document.body
  }

  /**
   * Preloads visual effects into memory
   * @returns Promise that resolves when all effects are loaded
   */
  public async preloadEffects(ids: string[]): Promise<void> {
    if (ids.length === 0) return
    const idsToLoad = ids.filter((id) => !this.effects.has(id))

    // First, import the effects
    const allSettled = await Promise.allSettled(idsToLoad.map(async (id) => await this.importEffect(id)))
    allSettled.forEach((outcome, index) => {
      if (outcome.status === 'fulfilled') {
        this.effects.set(ids[index], outcome.value)
      }
    })

    // For tsParticles effects, we set them up in advance
    for (const id of idsToLoad) {
      const effect = this.effects.get(id)
      if (effect?.type === 'tsParticles') {
        const { tsParticles } = await import('@tsparticles/engine')
        await effect.setup(tsParticles, true)
      }
    }
  }

  private async importEffect(id: string): Promise<VisualEffect> {
    const module = await import(`@@/bits/visual_effects/${id}.ts`)
    this.effects.set(id, module.default)
    return module.default
  }

  /**
   * Executes a visual effect by its ID with throttling (500ms)
   * @param id - ID of the effect to execute
   * @param options - Additional options for the effect
   * @returns Promise that resolves when the effect execution is complete
   */
  public executeEffect = throttle(
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    async (
      id: string,
      options?: VisualEffectExecuteOptions,
    ): Promise<{ averageFps: number; minFps: number; maxFps: number } | undefined> => {
      const currentCount = this.runningEffectCounts.get(id) ?? 0
      if (currentCount >= MAX_CONCURRENT_EFFECTS) return

      let effect = this.effects.get(id)
      if (effect == null) {
        effect = await this.importEffect(id)
      }

      const { FPSTracker } = await import('@@/bits/fps_tracker')
      const fpsTracker = new FPSTracker()
      fpsTracker.start()

      if (effect.type === 'tsParticles') {
        await this.executeTsParticlesEffect(effect, options)
      } else {
        await this.executeCustomEffect(effect, options)
      }

      fpsTracker.stop()
      return {
        averageFps: fpsTracker.getAverageFPS(),
        minFps: fpsTracker.getMinFPS(),
        maxFps: fpsTracker.getMaxFPS(),
      }
    },
    500,
    {
      leading: true,
      trailing: false,
    },
  )

  private async executeCustomEffect(effect: CustomVisualEffect, options?: VisualEffectExecuteOptions): Promise<void> {
    const target = this.getRenderTarget()
    const control = effect.execute(target, options)

    this.addRunningEffect(effect.id, control.stop)

    // Stop effect after duration
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        control.stop()
        resolve()
      }, effect?.duration ?? 2000)
    })

    try {
      // Wait for effect to complete, but not more than 50ms in case it's stuck
      const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 50))
      await Promise.race([control.promise, timeoutPromise])
    } finally {
      this.removeRunningEffect(effect.id, control.stop)
    }
  }

  private async executeTsParticlesEffect(
    effect: TsParticlesVisualEffect,
    options?: VisualEffectExecuteOptions,
  ): Promise<void> {
    // Always lazy load tsParticles engine
    const { tsParticles } = await import('@tsparticles/engine')
    const target = this.getRenderTarget()

    // tsParticles renders to the same `canvas` if we pass it the same element.
    // Create a new container `div` each time to avoid this.
    const childTarget = document.createElement('div')
    target.appendChild(childTarget)

    // If this is the first time playing this effect, set it up.
    const hasLoadedToTsParticles = tsParticles.getPreset(effect.id) != null
    if (!hasLoadedToTsParticles) {
      await effect.setup(tsParticles, false)
    }

    const instance = await tsParticles.load({
      id: `${effect.id}:${Date.now()}`, // Unique effect ID
      element: childTarget,
      options: {
        ...effect.getOptions(tsParticles, options),
        autoPlay: true,
      },
    })

    const stop = (): void => {
      instance?.stop()
      instance?.destroy(true)
      childTarget.remove()
    }
    this.addRunningEffect(effect.id, stop)

    try {
      // Wait for effect to complete, but not more than `maxDuration` in case it's stuck
      const timeoutPromise = new Promise((resolve) => setTimeout(resolve, effect.maxDuration ?? 5000))
      const runningPromise = checkOnNextPaintRecursively(() => instance == null || instance.destroyed)
      await Promise.race([runningPromise, timeoutPromise])
    } finally {
      stop()
      this.removeRunningEffect(effect.id, stop)
    }
  }

  /**
   * Stops all currently running visual effects
   */
  public stopAllEffects(): void {
    for (const stop of this.runningControls) {
      stop()
    }
    this.runningControls.clear()
    this.runningEffectCounts.clear()
  }
}

export const vfxManager = new VfxManager()
export type { VisualEffect, VisualEffectExecuteOptions }
